// pages/DiditCallback.tsx
//
// Callback page for Didit ID verification
// Receives verificationSessionId and status from Didit redirect
// Updates backend and then redirects to success/error page

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

const DiditCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lang = getLanguage();
  const labels = authLabels(lang).kyc?.callback ?? {};
  const fallbackLabels = authLabels("en").kyc?.callback ?? {};

  const verificationSessionId = searchParams.get("verificationSessionId");
  const status = searchParams.get("status"); // "Approved", "Declined", or "In Review"

  useEffect(() => {
    const processCallback = async () => {
      if (!verificationSessionId || !status) {
        setError(
          labels.missingParams ??
            fallbackLabels.missingParams ??
            "Missing parameters from Didit",
        );
        setIsProcessing(false);
        setTimeout(() => {
          navigate("/kyc/success?status=error");
        }, 2000);
        return;
      }

      try {
        // Call backend to update KYC status
        await api.post("/kyc/callback", {
          verificationSessionId,
          status,
        });

        // Map Didit status to our status
        const statusMap: Record<string, string> = {
          Approved: "success",
          Declined: "error",
          "In Review": "pending",
        };

        const mappedStatus = statusMap[status] || "error";

        // Redirect to success page with appropriate status
        navigate(`/kyc/success?status=${mappedStatus}`);
      } catch (err) {
        setError(
          labels.processingError ??
            fallbackLabels.processingError ??
            "An error occurred while processing the verification",
        );
        setIsProcessing(false);
        setTimeout(() => {
          navigate("/kyc/success?status=error");
        }, 2000);
      }
    };

    processCallback();
  }, [verificationSessionId, status, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4 relative">
      {/* Backdrop blur overlay */}
      <div className="absolute inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm" />

      <Card className="w-full max-w-md relative z-10 backdrop-blur-md bg-white/90 dark:bg-gray-800/90 border border-gray-200/50 dark:border-gray-700/50 shadow-2xl">
        <CardContent className="p-8">
          <div className="flex flex-col items-center justify-center space-y-6">
            <Loader2 className="h-20 w-20 text-blue-500 animate-spin" />
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                {labels.processingTitle ??
                  fallbackLabels.processingTitle ??
                  "Processing verification..."}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {error ||
                  (labels.waitingMessage ??
                    fallbackLabels.waitingMessage ??
                    "Waiting for verification result.")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiditCallback;
